import Dexie, { type Table } from 'dexie';
import { z } from 'zod';

export const CURRENT_SAVE_SCHEMA_VERSION = 6;
export const OBJECTIVE_CATALOG_VERSION = 1;
export const OBJECTIVE_IDS = [
  'active-operator',
  'boss-interceptor',
  'event-cartographer',
  'fusion-engineer',
  'curse-breaker',
  'thirty-wave-victor',
] as const;
export type ObjectiveId = (typeof OBJECTIVE_IDS)[number];
export const SEASON_CATALOG_VERSION = 1;
export const SEASON_IDS = ['voidline-s1'] as const;
export type SeasonId = (typeof SEASON_IDS)[number];
export const SEASON_CHALLENGE_IDS = [
  'skill-circuit',
  'boss-breach',
  'event-expedition',
  'fusion-trial',
] as const;
export type SeasonChallengeId = (typeof SEASON_CHALLENGE_IDS)[number];
export const PRIMARY_SAVE_SLOT = 'primary';
export const SAVE_EXPORT_FORMAT = 'rogue-brick-breaker-save';
export const DAILY_CHALLENGE_HISTORY_LIMIT = 400;

const campaignProgressSchema = z
  .object({
    highestCompletedLevel: z.number().int().nonnegative(),
    selectedLevel: z.number().int().positive(),
  })
  .strict();

const preferencesSchema = z
  .object({
    audioEnabled: z.boolean(),
    hapticsEnabled: z.boolean(),
    motionPreference: z.enum(['system', 'full', 'reduced']),
    tutorialComplete: z.boolean(),
  })
  .strict();

const careerProgressSchema = z
  .object({
    totalRuns: z.number().int().nonnegative(),
    victories: z.number().int().nonnegative(),
    defeats: z.number().int().nonnegative(),
    totalScore: z.number().int().nonnegative(),
    bestScore: z.number().int().nonnegative(),
    totalBricksDestroyed: z.number().int().nonnegative(),
    totalLevelsCleared: z.number().int().nonnegative(),
    totalPlaySeconds: z.number().int().nonnegative(),
    highestLevelReached: z.number().int().positive(),
    lastRunAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();

const dailyChallengeBestSchema = z
  .object({
    appBuildId: z.string().min(1).max(128),
    outcome: z.enum(['won', 'lost']),
    score: z.number().int().nonnegative(),
    reachedWave: z.number().int().min(1).max(30),
    wavesCleared: z.number().int().nonnegative().max(30),
    bricksDestroyed: z.number().int().nonnegative(),
    elapsedSeconds: z.number().int().nonnegative().max(86_400),
    completedAt: z.string().datetime({ offset: true }),
  })
  .strict();

const dailyChallengeRecordSchema = z
  .object({
    challengeId: z.string().regex(/^daily-\d{4}-\d{2}-\d{2}-r\d+-c\d+$/),
    date: z.string().date(),
    rulesVersion: z.number().int().positive(),
    contentVersion: z.number().int().positive(),
    appBuildId: z.string().min(1).max(128),
    seed: z.string().min(1).max(96),
    buildId: z.enum(['swarm', 'burst', 'fortress']),
    attempts: z.number().int().positive(),
    victories: z.number().int().nonnegative(),
    best: dailyChallengeBestSchema,
    lastPlayedAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((record, context) => {
    const expectedId = `daily-${record.date}-r${record.rulesVersion}-c${record.contentVersion}`;
    const expectedSeed = `DAILY-R${record.rulesVersion}-C${record.contentVersion}-${record.date.replaceAll('-', '')}`;
    if (record.challengeId !== expectedId) {
      context.addIssue({
        code: 'custom',
        path: ['challengeId'],
        message: '挑战 ID 与日期和版本不一致',
      });
    }
    if (record.seed !== expectedSeed) {
      context.addIssue({
        code: 'custom',
        path: ['seed'],
        message: '挑战种子与日期和版本不一致',
      });
    }
    if (record.victories > record.attempts) {
      context.addIssue({
        code: 'custom',
        path: ['victories'],
        message: '通关次数不能大于尝试次数',
      });
    }
  });

const dailyChallengeProgressSchema = z
  .object({ records: z.record(z.string(), dailyChallengeRecordSchema) })
  .strict()
  .superRefine((progress, context) => {
    for (const [recordKey, record] of Object.entries(progress.records)) {
      if (recordKey !== record.challengeId) {
        context.addIssue({
          code: 'custom',
          path: ['records', recordKey],
          message: '每日挑战记录键与挑战 ID 不一致',
        });
      }
    }
  });

const buildIdSchema = z.enum(['swarm', 'burst', 'fortress']);
const bossAffixIdSchema = z.enum(['armored-core', 'kinetic-overclock', 'resonant-defense']);

const buildChallengeBestSchema = z
  .object({
    appBuildId: z.string().min(1).max(128),
    outcome: z.enum(['won', 'lost']),
    completed: z.boolean(),
    score: z.number().int().nonnegative(),
    reachedWave: z.number().int().min(1).max(30),
    wavesCleared: z.number().int().nonnegative().max(30),
    bricksDestroyed: z.number().int().nonnegative(),
    elapsedSeconds: z.number().int().nonnegative().max(86_400),
    completedAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((best, context) => {
    if (
      best.completed &&
      (best.outcome !== 'won' || best.reachedWave !== 30 || best.wavesCleared !== 30)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['completed'],
        message: '构筑认证只能由完整 30 关胜利完成',
      });
    }
  });

const buildChallengeRecordSchema = z
  .object({
    recordId: z.string().regex(/^build-mastery:(swarm|burst|fortress):r\d+:c\d+$/),
    challengeId: z.literal('build-mastery'),
    rulesVersion: z.number().int().positive(),
    contentVersion: z.number().int().positive(),
    seed: z.string().min(1).max(96),
    buildId: buildIdSchema,
    attempts: z.number().int().positive(),
    completions: z.number().int().nonnegative(),
    best: buildChallengeBestSchema,
    firstCompletedAt: z.string().datetime({ offset: true }).nullable(),
    earnedBossAffixIds: z.array(bossAffixIdSchema).max(3),
    lastPlayedAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((record, context) => {
    const expectedRecordId = createBuildChallengeRecordId(
      record.buildId,
      record.rulesVersion,
      record.contentVersion,
    );
    const expectedSeed = `RBB-BUILD-MASTERY-R${record.rulesVersion}-C${record.contentVersion}`;
    if (record.recordId !== expectedRecordId) {
      context.addIssue({
        code: 'custom',
        path: ['recordId'],
        message: '构筑认证记录键与构筑和版本不一致',
      });
    }
    if (record.seed !== expectedSeed) {
      context.addIssue({
        code: 'custom',
        path: ['seed'],
        message: '构筑认证种子与版本不一致',
      });
    }
    if (record.completions > record.attempts) {
      context.addIssue({
        code: 'custom',
        path: ['completions'],
        message: '完成次数不能大于尝试次数',
      });
    }
    if (new Set(record.earnedBossAffixIds).size !== record.earnedBossAffixIds.length) {
      context.addIssue({
        code: 'custom',
        path: ['earnedBossAffixIds'],
        message: 'Boss 词缀徽章不能重复',
      });
    }
  });

const buildChallengeProgressSchema = z
  .object({ records: z.record(z.string(), buildChallengeRecordSchema) })
  .strict()
  .superRefine((progress, context) => {
    for (const [recordKey, record] of Object.entries(progress.records)) {
      if (recordKey !== record.recordId) {
        context.addIssue({
          code: 'custom',
          path: ['records', recordKey],
          message: '构筑认证记录键与记录 ID 不一致',
        });
      }
    }
  });

const objectiveIdSchema = z.enum(OBJECTIVE_IDS);

const objectiveProgressRecordSchema = z
  .object({
    progress: z.number().int().nonnegative(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();

const objectiveProgressSchema = z
  .object({
    catalogVersion: z.literal(OBJECTIVE_CATALOG_VERSION),
    records: z.partialRecord(objectiveIdSchema, objectiveProgressRecordSchema),
  })
  .strict();

const seasonIdSchema = z.enum(SEASON_IDS);
const seasonChallengeIdSchema = z.enum(SEASON_CHALLENGE_IDS);

const seasonChallengeRecordSchema = z
  .object({
    recordId: z
      .string()
      .regex(/^voidline-s1:v\d+:r\d+:(skill-circuit|boss-breach|event-expedition|fusion-trial)$/),
    seasonId: seasonIdSchema,
    seasonVersion: z.number().int().positive(),
    rotationIndex: z.number().int().nonnegative(),
    challengeId: seasonChallengeIdSchema,
    progress: z.number().int().nonnegative(),
    target: z.number().int().positive(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
    lastProgressAt: z.string().datetime({ offset: true }),
  })
  .strict()
  .superRefine((record, context) => {
    if (
      record.recordId !==
      createSeasonChallengeRecordId(
        record.seasonId,
        record.seasonVersion,
        record.rotationIndex,
        record.challengeId,
      )
    ) {
      context.addIssue({
        code: 'custom',
        path: ['recordId'],
        message: '赛季挑战记录键与版本和轮换身份不一致',
      });
    }
    if (record.progress > record.target) {
      context.addIssue({
        code: 'custom',
        path: ['progress'],
        message: '赛季挑战进度不能超过目标',
      });
    }
    if ((record.progress === record.target) !== Boolean(record.completedAt)) {
      context.addIssue({
        code: 'custom',
        path: ['completedAt'],
        message: '赛季挑战完成状态必须与目标进度一致',
      });
    }
  });

const seasonProgressSchema = z
  .object({
    catalogVersion: z.literal(SEASON_CATALOG_VERSION),
    records: z.record(z.string(), seasonChallengeRecordSchema),
  })
  .strict()
  .superRefine((progress, context) => {
    for (const [recordKey, record] of Object.entries(progress.records)) {
      if (recordKey !== record.recordId) {
        context.addIssue({
          code: 'custom',
          path: ['records', recordKey],
          message: '赛季挑战记录键与记录 ID 不一致',
        });
      }
    }
  });

export const gameSaveSchema = z
  .object({
    schemaVersion: z.literal(CURRENT_SAVE_SCHEMA_VERSION),
    campaign: campaignProgressSchema,
    preferences: preferencesSchema,
    career: careerProgressSchema,
    dailyChallenges: dailyChallengeProgressSchema,
    buildChallenges: buildChallengeProgressSchema,
    objectives: objectiveProgressSchema,
    seasons: seasonProgressSchema,
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict();

const legacySaveSchema = z
  .object({
    schemaVersion: z.literal(1),
    campaign: campaignProgressSchema.partial().optional(),
    preferences: preferencesSchema.partial().optional(),
  })
  .passthrough();

const schemaTwoSaveSchema = z
  .object({
    schemaVersion: z.literal(2),
    campaign: campaignProgressSchema,
    preferences: preferencesSchema,
    career: careerProgressSchema,
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict();

const schemaThreeSaveSchema = z
  .object({
    schemaVersion: z.literal(3),
    campaign: campaignProgressSchema,
    preferences: preferencesSchema,
    career: careerProgressSchema,
    dailyChallenges: dailyChallengeProgressSchema,
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict();

const schemaFourSaveSchema = z
  .object({
    schemaVersion: z.literal(4),
    campaign: campaignProgressSchema,
    preferences: preferencesSchema,
    career: careerProgressSchema,
    dailyChallenges: dailyChallengeProgressSchema,
    buildChallenges: buildChallengeProgressSchema,
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict();

const schemaFiveSaveSchema = z
  .object({
    schemaVersion: z.literal(5),
    campaign: campaignProgressSchema,
    preferences: preferencesSchema,
    career: careerProgressSchema,
    dailyChallenges: dailyChallengeProgressSchema,
    buildChallenges: buildChallengeProgressSchema,
    objectives: objectiveProgressSchema,
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict();

const saveExportSchema = z
  .object({
    format: z.literal(SAVE_EXPORT_FORMAT),
    exportedAt: z.string().datetime({ offset: true }),
    save: z.unknown(),
  })
  .strict();

export type GameSave = z.infer<typeof gameSaveSchema>;
export type CampaignSaveProgress = GameSave['campaign'];
export type GamePreferences = GameSave['preferences'];
export type CareerProgress = GameSave['career'];
export type DailyChallengeProgress = GameSave['dailyChallenges'];
export type DailyChallengeRecord = DailyChallengeProgress['records'][string];
export type BuildChallengeProgress = GameSave['buildChallenges'];
export type BuildChallengeRecord = BuildChallengeProgress['records'][string];
export type ObjectiveProgress = GameSave['objectives'];
export type ObjectiveProgressRecord = NonNullable<ObjectiveProgress['records'][ObjectiveId]>;
export type SeasonProgress = GameSave['seasons'];
export type SeasonChallengeRecord = SeasonProgress['records'][string];

export interface CareerRunResult {
  readonly outcome: 'won' | 'lost';
  readonly score: number;
  readonly level: number;
  readonly levelsCleared: number;
  readonly bricksDestroyed: number;
  readonly elapsedSeconds: number;
  readonly completedAt?: string;
}

export interface DailyChallengeRunResult {
  readonly challengeId: string;
  readonly date: string;
  readonly rulesVersion: number;
  readonly contentVersion: number;
  readonly appBuildId: string;
  readonly seed: string;
  readonly buildId: 'swarm' | 'burst' | 'fortress';
  readonly outcome: 'won' | 'lost';
  readonly score: number;
  readonly reachedWave: number;
  readonly wavesCleared: number;
  readonly bricksDestroyed: number;
  readonly elapsedSeconds: number;
  readonly completedAt?: string;
}

export interface BuildChallengeRunResult {
  readonly challengeId: 'build-mastery';
  readonly rulesVersion: number;
  readonly contentVersion: number;
  readonly appBuildId: string;
  readonly seed: string;
  readonly buildId: 'swarm' | 'burst' | 'fortress';
  readonly outcome: 'won' | 'lost';
  readonly completed: boolean;
  readonly score: number;
  readonly reachedWave: number;
  readonly wavesCleared: number;
  readonly bricksDestroyed: number;
  readonly elapsedSeconds: number;
  readonly earnedBossAffixIds?: readonly (
    'armored-core' | 'kinetic-overclock' | 'resonant-defense'
  )[];
  readonly completedAt?: string;
}

export interface ObjectiveProgressIncrement {
  readonly objectiveId: ObjectiveId;
  readonly amount: number;
  readonly target: number;
}

export interface ObjectiveProgressResult {
  readonly save: GameSave;
  readonly completedObjectiveIds: readonly ObjectiveId[];
}

export interface SeasonChallengeProgressIncrement {
  readonly seasonId: SeasonId;
  readonly seasonVersion: number;
  readonly rotationIndex: number;
  readonly challengeId: SeasonChallengeId;
  readonly amount: number;
  readonly target: number;
}

export interface SeasonChallengeProgressResult {
  readonly save: GameSave;
  readonly completedSeasonRecordIds: readonly string[];
}

export interface SaveSlotRecord {
  readonly slot: string;
  readonly payload: unknown;
  readonly updatedAt: string;
}

export interface SaveBackupRecord {
  readonly id?: number;
  readonly payload: unknown;
  readonly createdAt: string;
}

export interface RecoveryRecord {
  readonly id?: number;
  readonly source: 'primary' | 'backup' | 'import';
  readonly reason: string;
  readonly payload: unknown;
  readonly capturedAt: string;
}

export type SaveLoadStatus = 'created' | 'loaded' | 'migrated' | 'recovered';

export interface SaveLoadResult {
  readonly save: GameSave;
  readonly status: SaveLoadStatus;
  readonly message: string | null;
}

export class SaveValidationError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'SaveValidationError';
  }
}

export class GameDatabase extends Dexie {
  saves!: Table<SaveSlotRecord, string>;
  backups!: Table<SaveBackupRecord, number>;
  recovery!: Table<RecoveryRecord, number>;

  constructor(databaseName = 'rogue-brick-breaker') {
    super(databaseName);
    this.version(1).stores({
      settings: '&key',
      metadata: '&key',
    });
    this.version(2).stores({
      settings: null,
      metadata: '&key',
      saves: '&slot, updatedAt',
      backups: '++id, createdAt',
      recovery: '++id, capturedAt, source',
    });
  }
}

export function createDefaultGameSave(now = new Date().toISOString(), maxLevels = 30): GameSave {
  const safeMaxLevels = Math.max(1, Math.floor(maxLevels));
  return gameSaveSchema.parse({
    schemaVersion: CURRENT_SAVE_SCHEMA_VERSION,
    campaign: {
      highestCompletedLevel: 0,
      selectedLevel: Math.min(1, safeMaxLevels),
    },
    preferences: {
      audioEnabled: true,
      hapticsEnabled: true,
      motionPreference: 'system',
      tutorialComplete: false,
    },
    career: {
      totalRuns: 0,
      victories: 0,
      defeats: 0,
      totalScore: 0,
      bestScore: 0,
      totalBricksDestroyed: 0,
      totalLevelsCleared: 0,
      totalPlaySeconds: 0,
      highestLevelReached: 1,
      lastRunAt: null,
    },
    dailyChallenges: { records: {} },
    buildChallenges: { records: {} },
    objectives: { catalogVersion: OBJECTIVE_CATALOG_VERSION, records: {} },
    seasons: { catalogVersion: SEASON_CATALOG_VERSION, records: {} },
    updatedAt: now,
  });
}

export function migrateGameSave(
  input: unknown,
  options: { readonly maxLevels?: number; readonly now?: string } = {},
): { readonly save: GameSave; readonly migrated: boolean } {
  const maxLevels = Math.max(1, Math.floor(options.maxLevels ?? 30));
  const now = options.now ?? new Date().toISOString();
  const currentResult = gameSaveSchema.safeParse(input);
  if (currentResult.success) {
    return {
      save: normalizeGameSave(currentResult.data, maxLevels),
      migrated: false,
    };
  }

  const schemaFiveResult = schemaFiveSaveSchema.safeParse(input);
  if (schemaFiveResult.success) {
    return {
      migrated: true,
      save: gameSaveSchema.parse({
        ...schemaFiveResult.data,
        schemaVersion: CURRENT_SAVE_SCHEMA_VERSION,
        seasons: { catalogVersion: SEASON_CATALOG_VERSION, records: {} },
        updatedAt: now,
      }),
    };
  }

  const schemaFourResult = schemaFourSaveSchema.safeParse(input);
  if (schemaFourResult.success) {
    return {
      migrated: true,
      save: gameSaveSchema.parse({
        ...schemaFourResult.data,
        schemaVersion: CURRENT_SAVE_SCHEMA_VERSION,
        objectives: { catalogVersion: OBJECTIVE_CATALOG_VERSION, records: {} },
        seasons: { catalogVersion: SEASON_CATALOG_VERSION, records: {} },
        updatedAt: now,
      }),
    };
  }

  const schemaThreeResult = schemaThreeSaveSchema.safeParse(input);
  if (schemaThreeResult.success) {
    return {
      migrated: true,
      save: gameSaveSchema.parse({
        ...schemaThreeResult.data,
        schemaVersion: CURRENT_SAVE_SCHEMA_VERSION,
        buildChallenges: { records: {} },
        objectives: { catalogVersion: OBJECTIVE_CATALOG_VERSION, records: {} },
        seasons: { catalogVersion: SEASON_CATALOG_VERSION, records: {} },
        updatedAt: now,
      }),
    };
  }

  const schemaTwoResult = schemaTwoSaveSchema.safeParse(input);
  if (schemaTwoResult.success) {
    return {
      migrated: true,
      save: gameSaveSchema.parse({
        ...schemaTwoResult.data,
        schemaVersion: CURRENT_SAVE_SCHEMA_VERSION,
        dailyChallenges: { records: {} },
        buildChallenges: { records: {} },
        objectives: { catalogVersion: OBJECTIVE_CATALOG_VERSION, records: {} },
        seasons: { catalogVersion: SEASON_CATALOG_VERSION, records: {} },
        updatedAt: now,
      }),
    };
  }

  const legacyResult = legacySaveSchema.safeParse(input);
  if (!legacyResult.success) {
    throw new SaveValidationError('存档格式无效或版本不受支持', {
      cause: currentResult.error,
    });
  }

  const defaults = createDefaultGameSave(now, maxLevels);
  const legacy = legacyResult.data;
  const highestCompletedLevel = clampInteger(
    legacy.campaign?.highestCompletedLevel,
    0,
    maxLevels,
    defaults.campaign.highestCompletedLevel,
  );
  const highestUnlockedLevel = Math.min(maxLevels, highestCompletedLevel + 1);
  const selectedLevel = clampInteger(
    legacy.campaign?.selectedLevel,
    1,
    highestUnlockedLevel,
    defaults.campaign.selectedLevel,
  );

  return {
    migrated: true,
    save: gameSaveSchema.parse({
      ...defaults,
      campaign: { highestCompletedLevel, selectedLevel },
      preferences: {
        ...defaults.preferences,
        ...legacy.preferences,
      },
      updatedAt: now,
    }),
  };
}

export function recordCareerRun(save: GameSave, result: CareerRunResult): GameSave {
  const completedAt = result.completedAt ?? new Date().toISOString();
  const score = nonnegativeInteger(result.score);
  const level = Math.max(1, Math.floor(result.level));
  const levelsCleared = nonnegativeInteger(result.levelsCleared);
  const bricksDestroyed = nonnegativeInteger(result.bricksDestroyed);
  const elapsedSeconds = nonnegativeInteger(result.elapsedSeconds);
  return gameSaveSchema.parse({
    ...save,
    career: {
      totalRuns: save.career.totalRuns + 1,
      victories: save.career.victories + Number(result.outcome === 'won'),
      defeats: save.career.defeats + Number(result.outcome === 'lost'),
      totalScore: save.career.totalScore + score,
      bestScore: Math.max(save.career.bestScore, score),
      totalBricksDestroyed: save.career.totalBricksDestroyed + bricksDestroyed,
      totalLevelsCleared: save.career.totalLevelsCleared + levelsCleared,
      totalPlaySeconds: save.career.totalPlaySeconds + elapsedSeconds,
      highestLevelReached: Math.max(save.career.highestLevelReached, level),
      lastRunAt: completedAt,
    },
    updatedAt: completedAt,
  });
}

export function recordDailyChallengeRun(save: GameSave, result: DailyChallengeRunResult): GameSave {
  const completedAt = result.completedAt ?? new Date().toISOString();
  const candidate = dailyChallengeBestSchema.parse({
    appBuildId: result.appBuildId,
    outcome: result.outcome,
    score: nonnegativeInteger(result.score),
    reachedWave: Math.max(1, Math.floor(result.reachedWave)),
    wavesCleared: nonnegativeInteger(result.wavesCleared),
    bricksDestroyed: nonnegativeInteger(result.bricksDestroyed),
    elapsedSeconds: nonnegativeInteger(result.elapsedSeconds),
    completedAt,
  });
  const existing = save.dailyChallenges.records[result.challengeId];
  if (
    existing &&
    (existing.date !== result.date ||
      existing.rulesVersion !== result.rulesVersion ||
      existing.contentVersion !== result.contentVersion ||
      existing.seed !== result.seed ||
      existing.buildId !== result.buildId)
  ) {
    throw new SaveValidationError('每日挑战身份与已有本地记录冲突');
  }
  const best =
    existing && compareDailyBest(existing.best, candidate) >= 0 ? existing.best : candidate;
  const record = dailyChallengeRecordSchema.parse({
    challengeId: result.challengeId,
    date: result.date,
    rulesVersion: result.rulesVersion,
    contentVersion: result.contentVersion,
    appBuildId: result.appBuildId,
    seed: result.seed,
    buildId: result.buildId,
    attempts: (existing?.attempts ?? 0) + 1,
    victories: (existing?.victories ?? 0) + Number(result.outcome === 'won'),
    best,
    lastPlayedAt: completedAt,
  });
  const records = Object.fromEntries(
    Object.entries({ ...save.dailyChallenges.records, [result.challengeId]: record })
      .sort(([, left], [, right]) => right.date.localeCompare(left.date))
      .slice(0, DAILY_CHALLENGE_HISTORY_LIMIT),
  );
  return gameSaveSchema.parse({
    ...save,
    dailyChallenges: { records },
    updatedAt: completedAt,
  });
}

export function recordBuildChallengeRun(save: GameSave, result: BuildChallengeRunResult): GameSave {
  const completedAt = result.completedAt ?? new Date().toISOString();
  const recordId = createBuildChallengeRecordId(
    result.buildId,
    result.rulesVersion,
    result.contentVersion,
  );
  const candidate = buildChallengeBestSchema.parse({
    appBuildId: result.appBuildId,
    outcome: result.outcome,
    completed: result.completed,
    score: nonnegativeInteger(result.score),
    reachedWave: Math.max(1, Math.floor(result.reachedWave)),
    wavesCleared: nonnegativeInteger(result.wavesCleared),
    bricksDestroyed: nonnegativeInteger(result.bricksDestroyed),
    elapsedSeconds: nonnegativeInteger(result.elapsedSeconds),
    completedAt,
  });
  const existing = save.buildChallenges.records[recordId];
  if (
    existing &&
    (existing.challengeId !== result.challengeId ||
      existing.seed !== result.seed ||
      existing.buildId !== result.buildId)
  ) {
    throw new SaveValidationError('构筑认证身份与已有本地记录冲突');
  }
  const best =
    existing && compareBuildChallengeBest(existing.best, candidate) >= 0
      ? existing.best
      : candidate;
  const earnedBossAffixIds = [
    ...new Set([
      ...(existing?.earnedBossAffixIds ?? []),
      ...(result.completed ? (result.earnedBossAffixIds ?? []) : []),
    ]),
  ];
  const record = buildChallengeRecordSchema.parse({
    recordId,
    challengeId: result.challengeId,
    rulesVersion: result.rulesVersion,
    contentVersion: result.contentVersion,
    seed: result.seed,
    buildId: result.buildId,
    attempts: (existing?.attempts ?? 0) + 1,
    completions: (existing?.completions ?? 0) + Number(result.completed),
    best,
    firstCompletedAt: existing?.firstCompletedAt ?? (result.completed ? completedAt : null),
    earnedBossAffixIds,
    lastPlayedAt: completedAt,
  });
  return gameSaveSchema.parse({
    ...save,
    buildChallenges: {
      records: { ...save.buildChallenges.records, [recordId]: record },
    },
    updatedAt: completedAt,
  });
}

export function recordObjectiveProgress(
  save: GameSave,
  increments: readonly ObjectiveProgressIncrement[],
  completedAt = new Date().toISOString(),
): ObjectiveProgressResult {
  let records = save.objectives.records;
  const completedObjectiveIds: ObjectiveId[] = [];

  for (const increment of increments) {
    const amount = Math.floor(increment.amount);
    const target = Math.floor(increment.target);
    if (amount <= 0 || target <= 0) {
      throw new SaveValidationError('任务进度增量和目标必须为正整数');
    }
    const existing = records[increment.objectiveId];
    if (existing?.completedAt) {
      continue;
    }
    const progress = Math.min(target, (existing?.progress ?? 0) + amount);
    const newlyCompleted = progress >= target;
    records = {
      ...records,
      [increment.objectiveId]: {
        progress,
        completedAt: newlyCompleted ? completedAt : null,
      },
    };
    if (newlyCompleted) {
      completedObjectiveIds.push(increment.objectiveId);
    }
  }

  if (records === save.objectives.records) {
    return { save, completedObjectiveIds };
  }

  return {
    save: gameSaveSchema.parse({
      ...save,
      objectives: {
        catalogVersion: OBJECTIVE_CATALOG_VERSION,
        records,
      },
      updatedAt: completedAt,
    }),
    completedObjectiveIds,
  };
}

export function recordSeasonChallengeProgress(
  save: GameSave,
  increment: SeasonChallengeProgressIncrement,
  progressedAt = new Date().toISOString(),
): SeasonChallengeProgressResult {
  const amount = Math.floor(increment.amount);
  const target = Math.floor(increment.target);
  if (
    amount <= 0 ||
    target <= 0 ||
    increment.seasonVersion <= 0 ||
    !Number.isInteger(increment.seasonVersion) ||
    increment.rotationIndex < 0 ||
    !Number.isInteger(increment.rotationIndex)
  ) {
    throw new SaveValidationError('赛季挑战身份、进度增量和目标必须有效');
  }
  const recordId = createSeasonChallengeRecordId(
    increment.seasonId,
    increment.seasonVersion,
    increment.rotationIndex,
    increment.challengeId,
  );
  const existing = save.seasons.records[recordId];
  if (
    existing &&
    (existing.seasonId !== increment.seasonId ||
      existing.seasonVersion !== increment.seasonVersion ||
      existing.rotationIndex !== increment.rotationIndex ||
      existing.challengeId !== increment.challengeId ||
      existing.target !== target)
  ) {
    throw new SaveValidationError('赛季挑战身份或目标与已有记录冲突');
  }
  if (existing?.completedAt) {
    return { save, completedSeasonRecordIds: [] };
  }

  const progress = Math.min(target, (existing?.progress ?? 0) + amount);
  const newlyCompleted = progress >= target;
  const record = seasonChallengeRecordSchema.parse({
    recordId,
    seasonId: increment.seasonId,
    seasonVersion: increment.seasonVersion,
    rotationIndex: increment.rotationIndex,
    challengeId: increment.challengeId,
    progress,
    target,
    completedAt: newlyCompleted ? progressedAt : null,
    lastProgressAt: progressedAt,
  });

  return {
    save: gameSaveSchema.parse({
      ...save,
      seasons: {
        catalogVersion: SEASON_CATALOG_VERSION,
        records: { ...save.seasons.records, [recordId]: record },
      },
      updatedAt: progressedAt,
    }),
    completedSeasonRecordIds: newlyCompleted ? [recordId] : [],
  };
}

export function createSeasonChallengeRecordId(
  seasonId: SeasonId,
  seasonVersion: number,
  rotationIndex: number,
  challengeId: SeasonChallengeId,
): string {
  return `${seasonId}:v${Math.max(1, Math.floor(seasonVersion))}:r${Math.max(
    0,
    Math.floor(rotationIndex),
  )}:${challengeId}`;
}

export function createBuildChallengeRecordId(
  buildId: 'swarm' | 'burst' | 'fortress',
  rulesVersion: number,
  contentVersion: number,
): string {
  return `build-mastery:${buildId}:r${Math.max(1, Math.floor(rulesVersion))}:c${Math.max(
    1,
    Math.floor(contentVersion),
  )}`;
}

export function createSaveExport(save: GameSave, now = new Date().toISOString()): string {
  return JSON.stringify(
    {
      format: SAVE_EXPORT_FORMAT,
      exportedAt: now,
      save: gameSaveSchema.parse(save),
    },
    null,
    2,
  );
}

export function parseSaveImport(
  serialized: string,
  options: { readonly maxLevels?: number; readonly now?: string } = {},
): GameSave {
  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized) as unknown;
  } catch (error) {
    throw new SaveValidationError('无法解析存档文件', { cause: error });
  }
  const exportResult = saveExportSchema.safeParse(parsed);
  const payload = exportResult.success ? exportResult.data.save : parsed;
  return migrateGameSave(payload, options).save;
}

export class GameSaveRepository {
  readonly #database: GameDatabase;
  readonly #maxLevels: number;
  readonly #backupLimit: number;

  constructor(
    database = new GameDatabase(),
    options: { readonly maxLevels?: number; readonly backupLimit?: number } = {},
  ) {
    this.#database = database;
    this.#maxLevels = Math.max(1, Math.floor(options.maxLevels ?? 30));
    this.#backupLimit = Math.max(1, Math.floor(options.backupLimit ?? 3));
  }

  async loadOrCreate(legacyPayload?: unknown): Promise<SaveLoadResult> {
    const primary = await this.#database.saves.get(PRIMARY_SAVE_SLOT);
    if (!primary) {
      const migrated = legacyPayload === undefined ? null : this.#tryMigrate(legacyPayload);
      const save = migrated?.save ?? createDefaultGameSave(undefined, this.#maxLevels);
      await this.#writePrimary(save);
      return {
        save,
        status: migrated ? 'migrated' : 'created',
        message: migrated ? '旧版浏览器进度已迁移到 M7 存档' : null,
      };
    }

    const parsedPrimary = this.#tryMigrate(primary.payload);
    if (parsedPrimary) {
      if (parsedPrimary.migrated) {
        await this.#writePrimary(parsedPrimary.save);
      }
      return {
        save: parsedPrimary.save,
        status: parsedPrimary.migrated ? 'migrated' : 'loaded',
        message: parsedPrimary.migrated ? '存档已升级到最新版本' : null,
      };
    }

    await this.#quarantine('primary', '主存档校验失败', primary.payload);
    const backups = await this.#database.backups.orderBy('createdAt').reverse().toArray();
    for (const backup of backups) {
      const parsedBackup = this.#tryMigrate(backup.payload);
      if (!parsedBackup) {
        await this.#quarantine('backup', '备份存档校验失败', backup.payload);
        continue;
      }
      await this.#writePrimary(parsedBackup.save);
      return {
        save: parsedBackup.save,
        status: 'recovered',
        message: '主存档损坏，已从最近的有效备份恢复',
      };
    }

    const legacy = legacyPayload === undefined ? null : this.#tryMigrate(legacyPayload);
    const save = legacy?.save ?? createDefaultGameSave(undefined, this.#maxLevels);
    await this.#writePrimary(save);
    return {
      save,
      status: 'recovered',
      message: legacy
        ? '主存档损坏，已从旧版浏览器进度恢复'
        : '主存档和备份均不可用，已建立安全的新存档',
    };
  }

  async save(save: GameSave): Promise<GameSave> {
    const validated = normalizeGameSave(gameSaveSchema.parse(save), this.#maxLevels);
    const current = await this.#database.saves.get(PRIMARY_SAVE_SLOT);
    if (current) {
      const parsedCurrent = this.#tryMigrate(current.payload);
      if (parsedCurrent && JSON.stringify(parsedCurrent.save) !== JSON.stringify(validated)) {
        await this.#database.backups.add({
          payload: parsedCurrent.save,
          createdAt: new Date().toISOString(),
        });
        await this.#trimBackups();
      }
    }
    await this.#writePrimary(validated);
    return validated;
  }

  async import(serialized: string): Promise<GameSave> {
    let imported: GameSave;
    try {
      imported = parseSaveImport(serialized, { maxLevels: this.#maxLevels });
    } catch (error) {
      await this.#quarantine(
        'import',
        error instanceof Error ? error.message : '导入失败',
        serialized,
      );
      throw error;
    }
    return this.save({ ...imported, updatedAt: new Date().toISOString() });
  }

  async reset(): Promise<GameSave> {
    return this.save(createDefaultGameSave(undefined, this.#maxLevels));
  }

  async close(): Promise<void> {
    this.#database.close();
  }

  #tryMigrate(payload: unknown): ReturnType<typeof migrateGameSave> | null {
    try {
      return migrateGameSave(payload, { maxLevels: this.#maxLevels });
    } catch {
      return null;
    }
  }

  async #writePrimary(save: GameSave): Promise<void> {
    await this.#database.saves.put({
      slot: PRIMARY_SAVE_SLOT,
      payload: save,
      updatedAt: save.updatedAt,
    });
  }

  async #quarantine(
    source: RecoveryRecord['source'],
    reason: string,
    payload: unknown,
  ): Promise<void> {
    await this.#database.recovery.add({
      source,
      reason,
      payload,
      capturedAt: new Date().toISOString(),
    });
  }

  async #trimBackups(): Promise<void> {
    const backups = await this.#database.backups.orderBy('createdAt').reverse().toArray();
    const expiredIds = backups
      .slice(this.#backupLimit)
      .map((backup) => backup.id)
      .filter((id): id is number => id !== undefined);
    if (expiredIds.length > 0) {
      await this.#database.backups.bulkDelete(expiredIds);
    }
  }
}

function normalizeGameSave(save: GameSave, maxLevels: number): GameSave {
  const highestCompletedLevel = clampInteger(save.campaign.highestCompletedLevel, 0, maxLevels, 0);
  const highestUnlockedLevel = Math.min(maxLevels, highestCompletedLevel + 1);
  return gameSaveSchema.parse({
    ...save,
    campaign: {
      highestCompletedLevel,
      selectedLevel: clampInteger(save.campaign.selectedLevel, 1, highestUnlockedLevel, 1),
    },
  });
}

function clampInteger(
  value: number | undefined,
  minimum: number,
  maximum: number,
  fallback: number,
): number {
  const integer =
    typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : fallback;
  return Math.min(maximum, Math.max(minimum, integer));
}

function nonnegativeInteger(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

function compareDailyBest(
  left: z.infer<typeof dailyChallengeBestSchema>,
  right: z.infer<typeof dailyChallengeBestSchema>,
): number {
  const outcomeDifference = Number(left.outcome === 'won') - Number(right.outcome === 'won');
  if (outcomeDifference !== 0) {
    return outcomeDifference;
  }
  if (left.reachedWave !== right.reachedWave) {
    return left.reachedWave - right.reachedWave;
  }
  if (left.score !== right.score) {
    return left.score - right.score;
  }
  return right.elapsedSeconds - left.elapsedSeconds;
}

function compareBuildChallengeBest(
  left: z.infer<typeof buildChallengeBestSchema>,
  right: z.infer<typeof buildChallengeBestSchema>,
): number {
  const completionDifference = Number(left.completed) - Number(right.completed);
  if (completionDifference !== 0) {
    return completionDifference;
  }
  if (left.reachedWave !== right.reachedWave) {
    return left.reachedWave - right.reachedWave;
  }
  if (left.score !== right.score) {
    return left.score - right.score;
  }
  return right.elapsedSeconds - left.elapsedSeconds;
}
